import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { generateFilterPayloadForMedicalPaOrders } from "../utils/generateFilterPayloadForMedicalPaOrders";
import { generatePayloadForInitialDataFetch } from "../utils/generatePayloadForInitialDataFetch";
import { useDefaultFacetFiltersForRoute } from "./useDefaultFacetFiltersForRoute";
import { MedicalPaOrdersState } from "./useMedicalPaOrdersTableData";
import { useValidFilters } from "./useValidFilters";

/**
 * Manages the initial data fetch for the medical PA orders table on mount.
 * Generates payloads from persisted table context (search text, last batch,
 * sort field, sort direction) and valid filters, then triggers the batch fetch
 * once auth status options are available. Restoring `sortByField` / `sortAscending`
 * here ensures that returning to the table preserves the previously selected
 * server-side sort across remounts.
 *
 * @param setMedicalPaOrdersState - State setter for orders loading/data lifecycle
 * @param setTableData - State setter for table row data
 * @returns The completion flag, its setter, and a `refetch` that re-runs the
 *   `/search` call once with the current pre-filled payload without toggling
 *   the completion flag (avoiding a duplicate fetch from the filter/search effect)
 */
export const useInitialDataFetchingForTable = (
  setMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState(false);

  const orgId = getOrgIdForFetchExternalWorklist();
  const { getTableDataFromContext } = useTablesContext();
  const tableAttributes = getTableDataFromContext(
    TableNames.MEDICAL_PA_ORDERS_NEW,
  );
  const validFilters = useValidFilters();
  const defaultFacetFilters = useDefaultFacetFiltersForRoute();

  /**
   * Builds the batch payloads from the current table context (search text,
   * filters, sort, last batch) and triggers the `/search` fetch a single time.
   *
   * @returns void
   */
  const refetch = useCallback(() => {
    if (!authStatusOptions || authStatusOptions.length === 0) return;

    const payload = generateFilterPayloadForMedicalPaOrders(
      validFilters,
      tableAttributes?.searchedText ?? "",
      orgId,
      undefined,
      undefined,
      defaultFacetFilters,
    );
    const payloads = generatePayloadForInitialDataFetch(
      payload,
      tableAttributes?.lastBatchFetched ?? 0,
      tableAttributes?.searchedText ?? "",
      tableAttributes?.sortByField ?? "",
      tableAttributes?.sortAscending ?? true,
    );

    makePostCallForInitialDataFetch(
      payloads,
      authStatusOptions,
      setMedicalPaOrdersState,
      setTableData,
      setInitialDataFetchingExecuted,
    );
  }, [
    authStatusOptions,
    validFilters,
    orgId,
    defaultFacetFilters,
    tableAttributes,
    setMedicalPaOrdersState,
    setTableData,
  ]);

  useEffect(() => {
    if (
      !initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatusOptions, initialDataFetchingExecuted]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
    refetch,
  };
};
